
// Vue - Vuex
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter, State } from "vuex-class";

// Router
import router, { AppRoutes } from "@/router";

// Types
import { UserPermissions, RootState, QuickControlPreferences, Region } from "@/store/types";
import {
	ManualPulseRequest,
	ManualResetRequest,
	LinkedCamera,
	LinkedCameraStatus,
	LinkedCamerasForEvent,
	CameraPreviewRequest
} from "@/store/manual-actions/types";
import { ActionState } from "@/types/ActionState";
import { Response } from "@/store/responses/types";
import { GetReaderGroupsResponse } from "@/store/lockdown/types";
import { DeviceService } from "@sureview/v2-cameras";
import { DeviceSearchResult } from '@/store/devices/types';
import QuickControlResponse from "@/types/QuickControlResponse";
import { PrefTypesEnum } from '@/store/settings/types';
import { NotificationOptionType } from '@/types/NotifyType';

// Utils
import { getLocalRegion } from "@/utils/region-utils";

// Services
import ccureapi from "@/services/api.ccure.service";
import api from "@/services/api.service";
import { processLoggingPostDoorAction } from "@/services/postDoorActionLogging.service";

// Components
import ManualEventReset from "@/components/manual-actions/ManualEventReset.vue";
import ManualPulse from "@/components/manual-actions/ManualPulse.vue";
import ManualCameraPreview from "@/components/manual-actions/ManualCameraPreview.vue";
import RegionSelect from "@/components/form/RegionSelect.vue";
import ManualLockUnlock from "@/components/manual-actions/ManualLockUnlock.vue";
import OutputAction from '@/constants/OutputAction';

// Vuex modules
const Eventqueue = namespace("eventqueue");
const Devices = namespace("devices");
const ManualActions = namespace("manualaction");
const SiteMonitor = namespace("siteMonitor");
const Regions = namespace("regions");

@Component({
	components: {
		"manual-event-reset": ManualEventReset,
		"manual-pulse": ManualPulse,
		"camera-preview": ManualCameraPreview,
		"region-select": RegionSelect,
		"manual-lock-unlock": ManualLockUnlock,
	}
})
export default class ManualAction extends Vue {
	// -- Props -- //
	@Prop({ default: false, type: Boolean }) visible!: boolean;
	@Prop({ default: false, type: Boolean }) isPlugin!: boolean;
	@Prop({ default: true, type: Boolean }) canUserInteractWithPlugin: boolean;

	// -- Vuex -- //
	@State("User") currentUser: {
		userId: number;
		fullName: string;
	};

	@State((state: RootState) => state.User.quickControlMaxCameraRecordDuration)
	private quickControlMaxCameraRecordDuration: number;

	@State((state: RootState) => state.User.quickControlPrefs)
	private quickControlPrefs: QuickControlPreferences;

	@Getter getPermissions: UserPermissions;
	@Getter getFeature: (features: string[]) => boolean;

	@Eventqueue.Getter("getManualActionShown") manualActionShown!: boolean;
	@Eventqueue.Mutation setManualActionShown: (shown: boolean) => void;

	@Devices.Action deviceQuickControl: ({
		deviceId,
		reason,
		eventId,
		regionUrl
	}: {
		deviceId: number;
		reason: string;
		eventId: number;
		regionUrl: string;
	}) => Promise<QuickControlResponse>;

	@ManualActions.Getter getManualPulseData: ManualPulseRequest;
	@ManualActions.Getter getManualEventResetData: ManualResetRequest;
	@ManualActions.Action eventQuickControl: (data: ManualResetRequest) => Promise<any>;

	@SiteMonitor.Action createAuditRecord: ({ eventId, eventRecordTypeId, details, regionUrl }) => Promise<number>;

	@Regions.Getter private hasRegionsAvailable: boolean;

	// -- Properties -- //
	public manualPulseRequestData: ManualPulseRequest = {
		deviceId: null,
		reason: ""
	};

	public manualResetRequestData: ManualResetRequest = {
		responseId: null,
		groupId: null,
		serverId: null,
		cCureEventID: null,
		reason: ""
	};

	// The state of the pulse button
	public pulseState: ActionState = ActionState.Undefined;
	public resetState: ActionState = ActionState.Undefined;

	// List of linked cameras for relay or response selected
	public linkedCameras: LinkedCamera[] = [];

	// Event id of quick control event created for previewing a camera
	public quickControlEvent: number = -1;

	// Holds information about whether a camera has been viewed or failed to load
	public quickControlCamerasViewed: LinkedCameraStatus[] = [];

	public canCompleteQuickControl: boolean = false;

	private hasCompletedUnlock: boolean = false;

	public tabIndex: number = 0;

	private selectedDevice: DeviceSearchResult | null = null;

	// Possible refs / id's for the tabs
	readonly deviceTabId: string = "device";
	readonly eventsTabId: string = "event";
	// Stores the Id of the active tab
	public activeTabId: string = this.deviceTabId;

	// Possible values for the confirm button
	readonly confirmPulseText: string = "Momentary Unlock";
	readonly confirmResetText: string = "Reset Action";

	// Stores the actual value for the confirm button
	public confirmBtnText = this.confirmPulseText;

	// Indicates that we're loading the event search results
	public eventSearchLoading = false;

	public readerGroupLoading = false;

	// Constant to hold the server type id of a Ccure server
	readonly ccureServerTypeId: number = 1234;

	// Indicates if the data for the tabs is valid
	public isEventResetDataValid: boolean = false;
	public isPulseDataValid: boolean = false;

	private maxCameraDurationAlert: boolean = false;
	private showReaderGroupWarning: boolean = false;

	private maxCameraDurationTimeout: NodeJS.Timeout | null = null;

	private currentEvent: number = 0;

	private readerGroupResponse: GetReaderGroupsResponse | null = null;

	private region: Region = getLocalRegion();

	// -- Lifecycle Hooks -- //
	public async created(): Promise<void> {
		this.reset();
		await this.getPrefForPulseText();
	}

	private async getPrefForPulseText(): Promise<void> {
		const prefTypes = await api.getPrefsFromList([{ prefTypeID: PrefTypesEnum.QuickControlPulseWord, groupID: null }]);
		const pulseTextPref = prefTypes.find(x => x.prefTypeID == PrefTypesEnum.QuickControlPulseWord);
		if (pulseTextPref?.value == "true") {
			this.confirmBtnText = "Pulse";
		}
	}

	// -- Computed Properties -- //

	private get canUserInteractWith(): boolean {
		return (this.isPlugin && this.canUserInteractWithPlugin) || (!this.isPlugin && this.getPermissions?.canAccessQuickControl);
	}

	public get ccureUIQuickControlEventsEnabled(): boolean {
		return this.getFeature(["Integration", "CCure", "UI", "QuickControlEvents"]);
	}

	public get disableCloseAfterCompletion(): boolean {
		return this.getFeature(["Alarms", "QuickControl", "DisableCloseAfterCompletion"]);
	}

	public get openInNewWindow(): boolean {
		return this.getFeature(["Alarms", "QuickControl", "OpenInNewWindow"]);
	}

	public get isQuickControlCamerasEnabled(): boolean {
		return this.getFeature(["Alarms", "QuickControl", "Cameras"]);
	}

	public get isLockUnlockEnabled(): boolean {
		return this.getFeature(["Alarms", "QuickControl", "LockUnlockRelay"]) && (this.getPermissions?.canEditLockUnlock ?? false);
	}

	public get canQuickControlWithoutFootage(): boolean {
		return this.getPermissions.canPerformQuickControlWithoutFootage;
	}

	public get ccureControlQuickEventsPermission(): boolean {
		return this.getPermissions.canAccessQuickControlEvents;
	}

	/**
	 * Returns true if the user has access to any remote regions, and permission to perform a quick control in them.
	 */
	public get canQuickControlInRegions(): boolean {
		return this.hasRegionsAvailable && this.getPermissions.canQuickControlRegions;
	}

	/**
	 * Returns true if the user has access to any remote regions, and permission to perform a CCure reset in them.
	 */
	public get canCCureResetInRegions(): boolean {
		return this.hasRegionsAvailable && this.getPermissions.canResetCCureRegions;
	}

	/**
	 * Gets the enum so it can be referenced in the template.
	 */
	public get actionState(): typeof ActionState {
		return ActionState;
	}

	/**
	 * Returns true if the CCure integration is enabled, and the user has permission to reset CCure events through
	 * the Quick Control dialog.
	 */
	public get isCCure(): boolean {
		return this.ccureUIQuickControlEventsEnabled && this.ccureControlQuickEventsPermission;
	}


	private get isCcureRelay(): boolean {
		return this.selectedDevice?.serverTypeId === this.ccureServerTypeId ?? false;
	}

	private get deviceServerTypeId(): number {
		return this.selectedDevice?.serverTypeId ?? 0;
	}

	private get postDoorActionLoggingEnabled(): boolean {
		return this.getFeature(["Alarms", "QuickControl", "LogDoorAction"]);
	}

	/**
	 * Handler to reset the component when the user changes their selected region.
	 */
	@Watch("region")
	private onSelectedRegionChanged() {
		this.readerGroupLoading = false;
		this.eventSearchLoading = false;
		this.isEventResetDataValid = false;
		this.isPulseDataValid = false;
		this.maxCameraDurationAlert = false;
		this.hasCompletedUnlock = false;
		this.currentEvent = 0;
		this.clearMaxDurationTimeout();
		this.cameraPreviewReset();
	}

	/**
	 * Reset everything back it's original state.
	 */
	public reset() {
		this.activeTabId = this.deviceTabId;

		this.hasCompletedUnlock = false;

		this.currentEvent = 0;

		// Reset the tab back to initial value.
		this.tabIndex = 0;

		this.confirmBtnText = this.confirmPulseText;
		this.pulseState = ActionState.Undefined;
		this.resetState = ActionState.Undefined;
		this.isEventResetDataValid = false;
		this.isPulseDataValid = false;
		this.maxCameraDurationAlert = false;

		// Reset reader groups
		this.readerGroupResponse = null;

		this.clearMaxDurationTimeout();
	}

	private async CameraDurationAlert() {
		this.maxCameraDurationAlert = false;
		// We don't have this pref configured.
		if (!this.quickControlMaxCameraRecordDuration) return;

		this.maxCameraDurationTimeout = setTimeout(() => {
			this.maxCameraDurationAlert = true;
		}, this.quickControlMaxCameraRecordDuration * 1000);
	}

	private resetAll() {
		this.maxCameraDurationAlert = false;
		this.reset();
		this.cameraPreviewReset();
	}

	/**
	 * Creates a note in the system indicating that the user has manually reset a response / event
	 * and attaches the reason.
	 */
	public async resetAction() {
		if (!this.canResetActions) {
			return;
		}

		this.clearMaxDurationTimeout();

		this.resetState = ActionState.InProcess;

		try {
			this.manualResetRequestData = this.getManualEventResetData;

			// Call the method to reset
			await this.eventQuickControl({
				...this.manualResetRequestData,
				regionUrl: this.region.url
			});

			this.$notify({
				type: NotificationOptionType.Success,
				title: "Quick Control",
				text: "Operation Reset Action was successful"
			});

			if (!this.disableCloseAfterCompletion) {
				this.resetAll();
				this.setManualActionShown(false);
			} else {
				this.CameraDurationAlert();
				this.resetState = ActionState.Undefined;
				this.pulseState = ActionState.Undefined;
			}

			// If this is enabled, we are expecting to be in
			// another window, we will verify using the route path.
			if (
				this.openInNewWindow &&
				router.currentRoute.name == AppRoutes.QuickControl &&
				!this.disableCloseAfterCompletion
			) {
				window.close();
			}

			this.hasCompletedUnlock = true;
		} catch (err) {
			this.resetState = ActionState.Undefined;

			this.$notify({
				type: "error",
				title: "Quick Control - Reset Action",
				text: err
					? err.statusText
					: "Unable to reset the action - please contact support if this problem persists."
			});
		}
	}

	/**
	 * Creates a note in the system indicating that the user has pulsed the selected device,
	 * and attaches the reason. Then we issue a command to the device service indicating we wish
	 * to pulse a relay.
	 */
	public async pulse() {
		if (!this.canMomentarilyUnlock) {
			return;
		}

		this.clearMaxDurationTimeout();

		this.pulseState = ActionState.InProcess;

		try {
			this.manualPulseRequestData = this.getManualPulseData;
			// Get the details reqd to trigger the relay, and attach the note
			const quickControlResponse = await this.deviceQuickControl({
				deviceId: this.manualPulseRequestData.deviceId,
				reason: this.manualPulseRequestData.reason,
				eventId: this.currentEvent,
				regionUrl: this.region.url
			});

			this.currentEvent = quickControlResponse.eventId;

			const device = quickControlResponse.device as any;

			// If we got a device back, we can trigger it with the device service
			if (device && device.eventRecordID) {
				const deviceService = new DeviceService(device.deviceServerEndpoint);

				// Pulse the relay
				await deviceService.sendOutputCmd(
					device.deviceServiceSession,
					device.deviceID,
					null, // outputType does not exist in device model so pass null at the moment
					"pulse"
				);

				if (this.postDoorActionLoggingEnabled) {
					processLoggingPostDoorAction(
						OutputAction.Pulse,
						this.manualPulseRequestData.deviceId,
						this.deviceServerTypeId
					);
				}

				this.$notify({
					type: NotificationOptionType.Success,
					title: "Quick Control",
					text: `Operation ${this.confirmBtnText} was successful`
				});


				if (!this.disableCloseAfterCompletion) {
					this.resetAll();
					this.setManualActionShown(false);
				} else {
					this.CameraDurationAlert();
					this.resetState = ActionState.Undefined;
					this.pulseState = ActionState.Undefined;
				}

				// If this is enabled, we are expecting to be in
				// another window, we will verify using the route path.
				if (
					this.openInNewWindow &&
					router.currentRoute.name == AppRoutes.QuickControl &&
					!this.disableCloseAfterCompletion
				) {
					window.close();
				}

				this.hasCompletedUnlock = true;
			} else {
				// We haven't got any specific errors, so just throw null here
				// so we can hit the catch block and notify the user that something
				// has gone wrong
				throw null;
			}
		} catch (err) {
			this.pulseState = ActionState.Undefined;

			this.$notify({
				type: NotificationOptionType.Error,
				title: "Quick Control",
				text: err
					? err.statusText
					: "Unable to trigger the relay - please contact support if this problem persists."
			});
		}
	}

	/**
	 * Resets the modal back to it's original state and hides it.
	 */
	public cancel() {
		this.cameraPreviewReset();
		this.reset();
		this.setManualActionShown(false);

		// If this is enabled, we are expecting to be in
		// another window, we will verify using the route path.
		if (this.openInNewWindow && router.currentRoute.name == AppRoutes.QuickControl) {
			window.close();
		}
	}

	// Handles the tab being changed
	public tabChanged(event) {
		this.activeTabId = event.target.id;

		if (this.isQuickControlCamerasEnabled) {
			this.cameraPreviewReset();
		}
		this.readerGroupResponse = null;

		if (this.activeTabId === this.deviceTabId) {
			this.confirmBtnText = this.confirmPulseText;
		} else {
			this.confirmBtnText = this.confirmResetText;
		}
	}

	// Disables the button to output a pulse when evaluated to false
	public get canMomentarilyUnlock(): boolean {
		if (this.activeTabId !== this.deviceTabId) {
			return false;
		}

		if (!this.isQuickControlCamerasEnabled) {
			return this.isPulseDataValid;
		} else {
			return this.isPulseDataValid && (this.quickControlCameraViewed || this.canCompleteQuickControl);
		}
	}

	// Disables the button to reset actions when evaluated to false
	public get canResetActions(): boolean {
		if (this.activeTabId != this.eventsTabId) {
			return false;
		}

		if (!this.isQuickControlCamerasEnabled) {
			return this.isEventResetDataValid;
		} else {
			return this.isEventResetDataValid && (this.quickControlCameraViewed || this.canCompleteQuickControl);
		}
	}

	// Returns true when at least one camera has been viewed
	private get quickControlCameraViewed(): boolean {
		if (!this.quickControlCamerasViewed || !Object.keys(this.quickControlCamerasViewed).length) {
			return false;
		}
		return this.quickControlCamerasViewed.some(c => c.streamFailed == false && c.viewed == true);
	}

	public cameraPreviewReset() {
		this.linkedCameras = [];
		this.quickControlCamerasViewed = [];
		this.quickControlEvent = -1;
		this.canCompleteQuickControl = false;
		this.maxCameraDurationAlert = false;

		this.clearMaxDurationTimeout();
	}

	private clearMaxDurationTimeout() {
		if (this.maxCameraDurationTimeout) {
			clearTimeout(this.maxCameraDurationTimeout);
		}
	}

	/**
	 * Loads the cameras for the specified device/response
	 * Note, you should only pass in a deviceID or a response ID not both
	 * @param deviceId The device ID of the relay to load cameras for
	 * @param responseId The response id of the response to load cameras for
	 * @private
	 */
	private async loadCameraPreview(deviceId: number | null, responseId: number | null): Promise<void> {
		// Reset existing feed
		this.cameraPreviewReset();

		const payload: CameraPreviewRequest = {
			deviceId: deviceId,
			responseId: responseId
		};

		let result;
		// Grab linked cameras for relay
		if (deviceId) {
			result = await api.quickControlPreviewForDevice(payload, this.region.url);
		}
		// Grab linked cameras for response
		else {
			result = await api.quickControlPreviewForResponse(payload, this.region.url);
		}
		const data = result.data;

		// If the request fails or there are no cameras, allow user to complete the quick control
		if (data?.linkedCameras?.length == 0) {
			this.canCompleteQuickControl = true;
			return;
		}

		// New linked cameras to be previewed are updated
		await this.updateLinkedCameras(data);
	}

	private async loadReaderGroups(deviceId: number): Promise<void> {
		const { data: readerGroups } = await ccureapi.getReaderGroupsForDoor(deviceId, this.region?.url);
		this.readerGroupResponse = readerGroups;
		this.showReaderGroupWarning = this.showReaderGroupWarningFeature && this.isWarningReaderGroup;
	}

	public async deviceSelected(device: DeviceSearchResult): Promise<void> {
		this.selectedDevice = device;

		this.readerGroupResponse = null;
		this.showReaderGroupWarning = false;

		let loadCameraPreview: Promise<void> = null;
		let loadReaderGroup: Promise<void> = null;

		// Load the cameras if the feature is enabled and a device selected
		if (this.isQuickControlCamerasEnabled && device) {
			loadCameraPreview = this.loadCameraPreview(device.deviceId, null);
		}

		// Load the reader group name if the feature/prefs are enabled and the device is selected
		if (this.displayReaderGroupName && device) {
			this.readerGroupLoading = true;
			loadReaderGroup = this.loadReaderGroups(device.deviceId);
		}

		// Load all api requests at the same time
		// TODO replace with allSettled once typescript updated
		await loadCameraPreview;
		await loadReaderGroup;

		this.readerGroupLoading = false;
	}

	public async responseSelected(response: Response): Promise<void> {
		// Clear reader groups/warning
		this.readerGroupResponse = null;
		this.showReaderGroupWarning = false;

		// Load camera preview if enabled
		if (this.isQuickControlCamerasEnabled && response) {
			await this.loadCameraPreview(null, response.responseID);
		}
	}

	public async updateLinkedCameras(data: LinkedCamerasForEvent) {
		// When a user has this permission, they do not have to view a camera to perform quick control
		if (this.canQuickControlWithoutFootage) {
			this.canCompleteQuickControl = true;
			await this.createAuditRecord({
				eventId: data.eventId,
				eventRecordTypeId: 150,
				details: `User ${this.currentUser.fullName} is allowed to complete the quick action without viewing camera footage.`,
				regionUrl: this.region.url
			});
		}

		const cameras = data.linkedCameras.map(c => {
			return {
				deviceId: c.deviceId,
				title: c.title
			};
		});

		cameras.forEach(c => {
			this.quickControlCamerasViewed.push({
				deviceId: c.deviceId,
				streamFailed: false,
				viewed: false
			});
		});

		this.linkedCameras = cameras;
		this.quickControlEvent = data.eventId;
	}

	// Called when camera previewed, status updated based on success or failure
	public async cameraStreamedEvent(cameraStreamedStatus: LinkedCameraStatus) {
		let camera = this.quickControlCamerasViewed.find(c => c.deviceId == cameraStreamedStatus.deviceId);
		if (camera) {
			camera.viewed = cameraStreamedStatus.viewed;
			camera.streamFailed = cameraStreamedStatus.streamFailed;
			if (camera.viewed && !camera.streamFailed && !this.canCompleteQuickControl) {
				this.canCompleteQuickControl = true;
			}
		}

		// When all cameras fail, user can complete
		if (
			this.quickControlCamerasViewed.every(c => c.streamFailed == true) &&
			this.canCompleteQuickControl == false
		) {
			this.canCompleteQuickControl = true;
			await this.createAuditRecord({
				eventId: this.quickControlEvent,
				eventRecordTypeId: 148,
				details: `All cameras failed to stream, user ${this.currentUser.fullName} is allowed to complete the quick control`,
				regionUrl: this.region.url
			});
		}
	}

	private get confirmButtonDisabled() {
		return (
			(!this.canMomentarilyUnlock && !this.canResetActions) ||
			(this.activeTabId === this.deviceTabId &&
				(this.pulseState === this.actionState.InProcess || this.pulseState === this.actionState.Success)) ||
			(this.activeTabId === this.eventsTabId &&
				(this.resetState === this.actionState.InProcess || this.resetState === this.actionState.Success))
		);
	}

	private get warningRegex(): string {
		return this.quickControlPrefs.regex;
	}

	private get warningText(): string {
		return this.quickControlPrefs.warning;
	}

	private get isWarningReaderGroup(): boolean {
		const prefsSet = Boolean(this.warningRegex) && Boolean(this.warningText);
		const regexMatch = RegExp(this.warningRegex).test(this.readerGroupName);
		return regexMatch && prefsSet && this.showReaderGroupWarningFeature;
	}

	private get readerGroupName(): string {
		// null means there is no device selected or the reader group request is still loading
		if (!this.readerGroupResponse) {
			return "";
		}

		// Some error getting the reader groups
		if (!this.readerGroupResponse.success) {
			return "There was an error fetching the reader groups";
		}
		// No readers
		if (this.readerGroupResponse?.readerGroups?.length === 0) {
			return "No Reader Groups";
		}
		// Join reader groups into comma separated list
		const readerGroupNames = this.readerGroupResponse.readerGroups.map(rg => rg.name);
		return readerGroupNames.join(", ");
	}

	private get displayReaderGroupName() {
		return this.getFeature(["Alarms", "QuickControl", "ShowReaderGroups"]);
	}

	private get showReaderGroupWarningFeature(): boolean {
		return this.getFeature(["Alarms", "QuickControl", "ShowReaderGroupWarning"]);
	}

	private get readerGroupError(): string {
		return !this.readerGroupLoading && this.readerGroupResponse?.error ? this.readerGroupResponse.error : "";
	}
}
